import type * as api from '@gitbook/api-client';
import { confirm, toast } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { apiClient } from '../../../state';

export interface useSetSiteSpaceVisibilityProps {
    organization: api.Organization;
    site: api.Site;
    type: 'section' | 'variant';
}

type SiteSpaceOrSection = api.SiteSpace | api.SiteSection;

export function useSetSiteSpaceVisibility(props: useSetSiteSpaceVisibilityProps) {
    const { site, organization, type } = props;

    return useEventCallback((item: SiteSpaceOrSection) => {
        void confirm({
            title: `Toggle ${type} visibility in site navigation`,
            description: `Hidden ${type}s do not appear in the site navigation, but are still accessible via direct links and URLs.`,
            confirmLabel: item.hidden ? `Show ${type}` : `Hide ${type}`,
            onConfirm: async (_event, { preventClose }) => {
                await toast.catch(
                    async () => {
                        if (item.object === 'site-section') {
                            await apiClient.siteSections.updateSiteSectionById(
                                organization.id,
                                site.id,
                                item.id,
                                { hidden: !item.hidden },
                                { returnPreference: 'minimal' }
                            );
                        } else {
                            await apiClient.siteSpaces.updateSiteSpaceById(
                                organization.id,
                                site.id,
                                item.id,
                                { hidden: !item.hidden },
                                { returnPreference: 'minimal' }
                            );
                        }

                        const capitalizedType = type.charAt(0).toUpperCase() + type.slice(1);

                        toast.success(
                            item.hidden
                                ? `${capitalizedType} is now visible in site navigation`
                                : `${capitalizedType} is now hidden from site navigation`
                        );
                    },
                    `Error while updating ${type} visibility`,
                    { onReject: preventClose }
                );
            },
        });
    });
}
